import { LoadingOutlined } from "@ant-design/icons"
import { type BillingInvoice, BillingInvoiceLinks } from "@finway-group/shared/lib/models"
import { Button, Table, Tag } from "antd"
import { ColumnsType } from "antd/lib/table"
import moment from "moment"
import React, { useState } from "react"
import { Download as DownloadIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { PageBody } from "~Components/page/pageBody"
import { PriceLabel } from "~Components/priceLabel"
import { BILLING_INVOICES_DEFAULT_PAGE_SIZE, QueryOptions, useBillingInvoices } from "~Shared/queries/billing.queries"
import { PaymentSettingsTabRoutes, RoutePathEnum } from "~Shared/router/RoutePath.enum"
import { NotificationService } from "~Shared/services"
import { BillingSettingsService } from "~Shared/services/billingSettings.service"

interface PaymentInvoiceListProps {
    activeTab: PaymentSettingsTabRoutes
}

export const BillingInvoiceList = ({ activeTab }: PaymentInvoiceListProps) => {
    const { t } = useTranslation()
    const isEnabled = activeTab === RoutePathEnum.SETTINGS_BILLING_TAB_INVOICES
    const [queryOptions, setQueryOptions] = useState<QueryOptions<BillingInvoice>>({
        sorting: { order: "descend", field: "created" },
        page: 1,
        limit: BILLING_INVOICES_DEFAULT_PAGE_SIZE,
        isEnabled,
    })
    const { data: invoices, isFetching } = useBillingInvoices(queryOptions)

    const getInvoiceLinksAndOpen = (invoice: BillingInvoice, link: keyof BillingInvoiceLinks) => {
        BillingSettingsService.getInvoiceLinks(invoice)
            .then((data) => {
                window.open(data[link], "_blank", "noopener,noreferrer")
            })
            .catch((fetchInvoiceLinksError: unknown) => {
                NotificationService.showErrorNotificationBasedOnResponseError(fetchInvoiceLinksError, t("error:request:not_found.title"), t("error:request:not_found.message"))
            })
    }

    const columns: ColumnsType<BillingInvoice> = [
        {
            title: t("label:payment_settings.invoice_no"),
            key: "invoiceNumber",
            width: 100,
            sorter: true,
            render: (invoice: BillingInvoice) => invoice.invoiceNumber,
        },
        {
            title: t("label:date"),
            defaultSortOrder: "descend",
            key: "created",
            width: 100,
            sorter: true,
            render: (invoice: BillingInvoice) => <>{moment(invoice.created).format("ll")}</>,
        },
        {
            title: t("label:status"),
            key: "status",
            width: 100,
            sorter: true,
            render: (invoice: BillingInvoice) => <Tag>{invoice.status}</Tag>,
        },
        {
            title: t("label:total"),
            key: "amountDue",
            width: 100,
            sorter: true,
            align: "right",
            render: (invoice: BillingInvoice) => <PriceLabel value={invoice.amountDue / 100} currency={invoice.currency} />,
        },
        {
            title: t("label:pdf"),
            key: "stripeHostedPdf",
            width: 40,
            sorter: false,
            align: "right",
            render: (invoice: BillingInvoice) => (
                <span className="ml-20">
                    <Button onClick={() => getInvoiceLinksAndOpen(invoice, "stripeHostedPdf")}>
                        <DownloadIcon />
                    </Button>
                </span>
            ),
        },
    ]

    return (
        <PageBody>
            <Table<BillingInvoice>
                rowKey={(record) => record.invoiceId}
                columns={columns}
                dataSource={invoices?.docs}
                loading={{
                    spinning: isFetching,
                    indicator: <LoadingOutlined style={{ fontSize: 30, color: "black" }} spin />,
                }}
                pagination={{
                    position: ["bottomRight"],
                    showSizeChanger: true,
                    total: invoices?.totalDocs,
                    hideOnSinglePage: false,
                    pageSizeOptions: ["5", "10", "20", "50", "100"],
                    defaultPageSize: BILLING_INVOICES_DEFAULT_PAGE_SIZE,
                }}
                onChange={(pagination, _filters, sorter: any) => {
                    setQueryOptions({
                        sorting: { order: sorter.order, field: sorter.columnKey },
                        limit: pagination.pageSize ?? BILLING_INVOICES_DEFAULT_PAGE_SIZE,
                        page: pagination.current ?? 1,
                        isEnabled,
                    })
                }}
                onRow={(invoice) => ({
                    onClick: () => {
                        getInvoiceLinksAndOpen(invoice, "stripeHostedUrl")
                    },
                })}
            />
        </PageBody>
    )
}
